/**
 * The content schema — for `content/mapa.json` on disk and for every revision published from the
 * world builder, which are the same shape by construction.
 *
 * The JSON keys are Serbian on purpose: this is curriculum content authored by the Serbian-speaking
 * team, now through a form rather than a text editor. Everything else in the codebase — types,
 * functions, variables — is English. The `message` strings in `validateContent` are the exception,
 * and the comment there says why.
 *
 * Extensibility contract (this is the whole point of the shape):
 *   - Islands are an append-only array. Adding island 3..12 is a new object, nothing else.
 *   - A path (adobe / davinci / kreator) is a key in `putanje`. Adding a path is a new key plus
 *     per-path values on the nodes that differ. No node duplication, no schema change.
 *   - Any node field can be either a scalar (identical for every path) or a `{ [path]: value }`
 *     map with an optional `svi` fallback. `perPath()` resolves both.
 *   - Node ids are opaque, globally unique and permanent. Progress in the database references
 *     them as strings, so content can be reshaped without a database migration. A node that
 *     disappears keeps its progress rows, and those works still count (`countWorks` in rules.ts).
 *
 * This schema is the gate: the publish endpoint parses the exact bytes it is about to store, so
 * content that needs code this deployment does not have cannot reach a student.
 */

import { z } from 'zod';

import { decodeTerrain, MAX_ISLAND_SIZE, MIN_ISLAND_SIZE } from './terrain';

export const PATH_IDS = ['adobe', 'davinci', 'kreator'] as const;
export type PathId = (typeof PATH_IDS)[number];

export const NODE_KINDS = ['lekcije', 'vezba', 'veliki', 'riznica', 'opciono'] as const;
export type NodeKind = (typeof NODE_KINDS)[number];

export const SUBMISSION_KINDS = ['nista', 'link', 'link+popravka'] as const;
export type SubmissionKind = (typeof SUBMISSION_KINDS)[number];

/**
 * The people who can stand on an island, and the things that can be put on it.
 *
 * These lists live here rather than beside the art for the same reason the terrain enum lives in
 * core: the publish gate has to be able to refuse a character nobody has drawn, and the gate runs
 * where there is no canvas. `src/ui/art/characters.ts` implements exactly this list — the Record
 * type there makes a missing drawing a compile error rather than an invisible person.
 */
export const CHARACTER_IDS = ['marko', 'mario'] as const;
export type CharacterId = (typeof CHARACTER_IDS)[number];

/**
 * Everything that can stand on an island.
 *
 * Append-only, and the reason is the same as everywhere else in this file: an id is stored in
 * published content and in every member's own island, so reordering the list would repaint them all.
 * The first seven were drawn for the world builder; the rest arrived with MOJE OSTRVO, where a member
 * furnishing a place they live in needs more than a barrel and a lighthouse.
 *
 * One registry, both palettes. A hammock on TEMELJ harms nobody, and a second list would be a second
 * thing to keep in step.
 */
export const PROP_IDS = [
  'svetionik',
  'vatra',
  'bure',
  'sanduk',
  'tabla',
  'barka',
  'palma',
  'stolica',
  'sto',
  'radni-sto',
  'polica',
  'lampa',
  'fenjer',
  'klupa',
  'suncobran',
  'ljuljaska',
  'satar',
  'bunar',
  'saksija',
  'panj',
  'pecurka',
  'skoljka',
  'bova',
  'mreze',
  'kip',
  'merdevine',
  'bubanj',
  'gajbe',
  'stub',
  'zastava',
] as const;
export type PropId = (typeof PROP_IDS)[number];

export const FACINGS = ['gore', 'dole', 'levo', 'desno'] as const;
export type FacingId = (typeof FACINGS)[number];

/**
 * Path ids and per-path map keys. Plain lowercase slugs, plus `svi` and `$`-prefixed comment
 * keys where authoring uses them. Deliberately excludes `__proto__` and friends: content is
 * runtime data edited in the app now, and `perPath` reads these maps by key — a hostile key
 * must be unrepresentable, not merely unlikely.
 */
export const PATH_KEY_RE = /^[a-z][a-z0-9-]{0,29}$/;
const pathKey = z.string().regex(PATH_KEY_RE);
const perPathKey = z.string().regex(/^(\$[A-Za-z0-9_-]{1,40}|[a-z][a-z0-9-]{0,29})$/);

/**
 * Island and node ids. Constrained for the same reason as the path keys, and more urgently: node
 * ids are the KEYS of the per-player progress record (`db.ts` builds a plain object from them), so
 * an id like `__proto__` would set that object's prototype instead of a row — the work would
 * vanish from the state the client receives — and an id like `toString` would read as truthy for
 * every player who has no row at all, quietly cancelling `unaprijedZavrsen`.
 *
 * Every id in the shipped content already matches, `temelj.BOSS` included. Ids are permanent, so
 * this only ever constrains ids being created from now on.
 */
const contentId = z
  .string()
  .regex(/^[A-Za-z][A-Za-z0-9._-]{0,63}$/, 'id: slova, cifre, . _ - (počinje slovom)')
  .refine((id) => !(id in Object.prototype), 'taj id je rezervisan');

/** Admin-authored URLs must be web URLs. `z.url()` alone accepts `javascript:` — see links.ts,
 *  which already refuses non-http(s) schemes for the far less privileged student input. */
const webUrl = z
  .string()
  .url()
  .refine((value) => /^https?:\/\//i.test(value), 'mora da počne sa https://');

/** A value that is either the same for every path, or given per path with an optional `svi`. */
const perPathOf = <T extends z.ZodTypeAny>(inner: T) =>
  z.union([inner, z.record(perPathKey, inner)]);

const skoolRef = z.object({
  course: z.string().min(1),
  lessons: z.array(z.string()).default([]),
  /** Optional: overrides the generated deep link entirely. */
  url: webUrl.optional(),
});

const noteSchema = z.object({
  tekst: z.string().min(1),
  ko: z.string().min(1).optional(),
});

const nodeSchema = z
  .object({
    id: contentId,
    tip: z.enum(NODE_KINDS),
    naslov: perPathOf(z.string().min(1)),
    /** Real minutes. Lessons = sum of Skool videoLenMs. Exercises = actual working time. */
    estMin: perPathOf(z.number().int().min(0).max(1440)),
    predaja: z.enum(SUBMISSION_KINDS).default('nista'),
    /** Node ids that must be complete first. Must reference nodes on the same island. */
    zahteva: z.array(z.string()).default([]),
    /** Only these paths see the node. Omitted = every path the island belongs to. */
    putanje: z.array(z.string()).optional(),
    /** A principle that carries across programs; stays credited when the student switches path. */
    univerzalno: z.boolean().optional(),
    /** Endowed progress (Nunes & Drèze): counted done the moment the account exists. */
    unaprijedZavrsen: z.boolean().optional(),
    /** What to actually do — shown on submission nodes. */
    zadatak: perPathOf(z.string()).optional(),
    /** Curated note from the community, shown on the node. */
    napomena: noteSchema.optional(),
    /**
     * Editorial horizon, free islands only: this node is not OFFERED until that island has opened.
     *
     * Not a lock and never rendered — the same "nothing" the map horizon uses. It reads
     * `openedIslands`, which is sticky and never withdrawn, so visibility is monotone: an exercise
     * that has been offered can never disappear again. A node may carry this or a non-empty
     * `zahteva`, never both (checked at publish) — that is "one key" pushed down to node scale.
     */
    posle: z.string().optional(),
    skool: z.record(perPathKey, skoolRef).optional(),
  })
  .passthrough();

/* ------------------------------------------------------------------ the authored world */

/**
 * An island the admin drew, rather than one the generator grew.
 *
 * Everything in here is optional at the island level, and that is the whole compatibility story:
 * an island with no `svet` is generated from its node list exactly as it always was, so this
 * schema change repaints nothing. The world builder writes a `svet` the first time somebody
 * paints on that island, and from then on the drawing wins.
 *
 * Coordinates are island-local — 0,0 is the top-left of this island's own canvas — and the island
 * is placed on the archipelago by `mapa`, which stays the island's CENTRE. That is what lets an
 * island be switched from generated to drawn without it jumping to a different sea.
 */
const tileRef = z.object({
  x: z.number().int().min(0).max(MAX_ISLAND_SIZE - 1),
  y: z.number().int().min(0).max(MAX_ISLAND_SIZE - 1),
});

const characterSchema = z
  .object({
    id: contentId,
    /** Which drawing. Refused at publish if nobody has drawn it. */
    lik: z.enum(CHARACTER_IDS),
    x: z.number().int().min(0).max(MAX_ISLAND_SIZE - 1),
    y: z.number().int().min(0).max(MAX_ISLAND_SIZE - 1),
    gleda: z.enum(FACINGS).default('dole'),
    /** Overrides the drawing's own name, for when the same face plays two parts. */
    ime: z.string().min(1).max(24).optional(),
    /** One box of text each, shown in order. Serbian — a student reads these. */
    poruke: z.array(z.string().min(1).max(240)).min(1).max(6),
  })
  .passthrough();

const propSchema = z
  .object({
    vrsta: z.enum(PROP_IDS),
    x: z.number().int().min(0).max(MAX_ISLAND_SIZE - 1),
    y: z.number().int().min(0).max(MAX_ISLAND_SIZE - 1),
  })
  .passthrough();

const worldSchema = z
  .object({
    sirina: z.number().int().min(MIN_ISLAND_SIZE).max(MAX_ISLAND_SIZE),
    visina: z.number().int().min(MIN_ISLAND_SIZE).max(MAX_ISLAND_SIZE),
    /** Run-length terrain — see `encodeTerrain`. Checked against sirina × visina at publish. */
    teren: z.string().min(1).max(20_000),
    /** Where the player lands. Everything on the island is reachable from here, or it does not publish. */
    dok: tileRef,
    /** Node id → the tile its marker stands on. Every node on the island needs one. */
    mesta: z.record(contentId, tileRef).default({}),
    likovi: z.array(characterSchema).max(24).default([]),
    ukrasi: z.array(propSchema).max(64).default([]),
  })
  .passthrough();

const islandSchema = z
  .object({
    id: contentId,
    naziv: z.string().min(1),
    redosled: z.number().int().min(0),
    putanje: z.array(z.string()).min(1),
    /** Skool's own time lock: day of membership. Cannot be bypassed (GDD §4). */
    dan: z.number().int().min(0).default(0),
    /** The game's own lock: works posted to the community. */
    potrebnoRadova: z.number().int().min(0).default(0),
    suvenir: z.string().nullable().default(null),
    /**
     * A place, not a step. A free island sits beside the chain: it has no predecessor and is never
     * anybody's predecessor, it has no denominator, it is never cleared and it grants no souvenir.
     *
     * That is what makes adding an exercise cost nothing — see `islandProgress` and the theorem in
     * docs/VEZBE.md §3. Everything else about it is an ordinary island.
     */
    slobodno: z.boolean().optional(),
    /** One sentence: what the student walks away with. */
    cilj: z.string().optional(),
    /** Visual theme id, resolved by the renderer. Falls back to a deterministic pick. */
    biom: z.string().optional(),
    /** Position on the world map, in tiles. Auto-laid-out when absent. */
    mapa: z.object({ x: z.number(), y: z.number() }).optional(),
    /** Drawn in the world builder. Absent = the island is generated from its nodes. */
    svet: worldSchema.optional(),
    cvorovi: z.array(nodeSchema).min(1),
  })
  .passthrough();

export const contentSchema = z
  .object({
    verzija: z.number().int().min(1).default(1),
    lessonUrlTemplate: z
      .string()
      .refine(
        (value) => value.startsWith('https://') && value.includes('{course}'),
        'mora da počne sa https:// i sadrži {course}',
      )
      .default('https://www.skool.com/talasakademija/classroom/{course}?md={lesson}'),
    zajednicaUrl: webUrl.default('https://www.skool.com/talasakademija'),
    putanje: z.record(
      pathKey,
      z.object({
        naziv: z.string().min(1),
        opis: z.string().default(''),
        /** false = visible but not selectable yet (content still missing). */
        aktivna: z.boolean().default(true),
        /** Shown instead of the description when `aktivna` is false. */
        napomena: z.string().optional(),
      }),
    ),
    ostrva: z.array(islandSchema).min(1),
  })
  .passthrough();

export type RawContent = z.infer<typeof contentSchema>;
export type RawIsland = z.infer<typeof islandSchema>;
export type RawNode = z.infer<typeof nodeSchema>;
export type RawWorld = z.infer<typeof worldSchema>;
export type RawCharacter = z.infer<typeof characterSchema>;
export type RawProp = z.infer<typeof propSchema>;

/* ------------------------------------------------------------------ resolution */

/** Resolve a scalar-or-per-path value for one path. Own keys only — never the prototype chain. */
export function perPath<T>(value: unknown, path: string, fallback?: T): T {
  if (value === null || value === undefined) return fallback as T;
  if (typeof value !== 'object' || Array.isArray(value)) return value as T;
  const map = value as Record<string, T>;
  if (Object.hasOwn(map, path)) return map[path] as T;
  if (Object.hasOwn(map, 'svi')) return map.svi as T;
  const first = Object.keys(map).find((k) => !k.startsWith('$'));
  return first ? (map[first] as T) : (fallback as T);
}

export interface ResolvedNode {
  id: string;
  islandId: string;
  kind: NodeKind;
  title: string;
  estMin: number;
  submission: SubmissionKind;
  requires: string[];
  universal: boolean;
  preCompleted: boolean;
  task: string | null;
  note: { tekst: string; ko?: string } | null;
  /** Deep link into Skool, or null when the node has no lesson behind it. */
  skoolUrl: string | null;
  /** Every lesson link, when a node covers several lessons. */
  lessonUrls: string[];
  /** True for `opciono` — excluded from the island's denominator. */
  optional: boolean;
  /** True for nodes that put a work into the community. */
  posts: boolean;
  /** True for nodes whose fourth step is the revision. */
  needsRevision: boolean;
  /** Island id this node waits for before being offered, or null. Never a lock — see `posle`. */
  after: string | null;
}

export interface ResolvedCharacter {
  id: string;
  islandId: string;
  art: CharacterId;
  /** Island-local tile. */
  x: number;
  y: number;
  facing: FacingId;
  name: string | null;
  lines: string[];
}

export interface ResolvedProp {
  kind: PropId;
  x: number;
  y: number;
}

/**
 * The drawn island, decoded once. `tiles` is width × height of `Terrain`, island-local.
 *
 * Null on every island nobody has drawn — and also on an island whose stored terrain no longer
 * decodes, which is why the renderer branches on this object rather than on the presence of the
 * `svet` key. Unreadable terrain falls back to the generator; it looks wrong, and it works.
 */
export interface IslandWorld {
  width: number;
  height: number;
  tiles: Uint8Array;
  dock: { x: number; y: number };
  /** Node id → island-local tile. Nodes with no entry fall back to the serpentine layout. */
  spots: Map<string, { x: number; y: number }>;
  characters: ResolvedCharacter[];
  props: ResolvedProp[];
}

export interface ResolvedIsland {
  id: string;
  name: string;
  order: number;
  day: number;
  worksRequired: number;
  souvenir: string | null;
  goal: string | null;
  biome: string;
  /** Off the chain: a place you can go, never a step you must take. See `slobodno`. */
  free: boolean;
  map: { x: number; y: number };
  /** The authored terrain, or null when this island is still generated from its node list. */
  world: IslandWorld | null;
  nodes: ResolvedNode[];
  /**
   * Every node on this island, in authored order, across ALL paths — including the ones this path
   * cannot see.
   *
   * An island is a place, and a place does not change shape depending on which program you edit in.
   * The generator lays the island out from this list rather than from `nodes`, which is what makes
   * two members on different paths stand on the same coastline, walk the same trail, and find a
   * shared task on the same tile — the „identične pozicije čvorova" GDD §5.1 asks for and the
   * generator used not to deliver. The world builder has always seeded its canvas from every node
   * for the same reason; this is the runtime finally agreeing with it.
   *
   * `nodes` still decides which of those tiles gets a signpost. A task only ADOBE has leaves a spot
   * on the trail that a DaVinci member simply walks over.
   */
  layoutIds: readonly string[];
}

export interface ResolvedContent {
  version: number;
  communityUrl: string;
  paths: Array<{ id: string; name: string; description: string; active: boolean; note?: string }>;
  islands: ResolvedIsland[];
  /** Every node id that exists anywhere in the content, across all paths. */
  allNodeIds: Set<string>;
}

const BIOMES = ['plicak', 'temelj', 'rez', 'pokret', 'forma', 'klijent', 'spot', 'magija'];

/** The schema already refuses non-web URLs; this is the runtime backstop for stored content
 *  that predates the check. A dead button beats a live `javascript:` href. */
function safeWebUrl(url: string): string | null {
  return /^https?:\/\//i.test(url.trim()) ? url.trim() : null;
}

function buildLessonUrls(node: RawNode, path: string, template: string): string[] {
  const ref = node.skool ? perPath<z.infer<typeof skoolRef> | undefined>(node.skool, path) : undefined;
  if (!ref || !ref.course) return [];
  if (ref.url) {
    const safe = safeWebUrl(ref.url);
    return safe ? [safe] : [];
  }
  if (!ref.lessons || ref.lessons.length === 0) {
    const url = safeWebUrl(template.replace('{course}', ref.course).replace('?md={lesson}', ''));
    return url ? [url] : [];
  }
  return ref.lessons
    .map((lesson) => safeWebUrl(template.replace('{course}', ref.course).replace('{lesson}', lesson)))
    .filter((url): url is string => url !== null);
}

/**
 * Decode one island's drawing, or return null and let the generator have it.
 *
 * Everything that can be wrong here — terrain that does not decode, a dock outside the canvas —
 * is already an error at the publish gate, so reaching those branches means serving content that
 * predates the check or was written by hand. Falling back beats throwing: the island renders
 * generated, the student keeps playing, and the checker says exactly what to fix.
 */
export function resolveIslandWorld(island: RawIsland): IslandWorld | null {
  const authored = island.svet;
  if (!authored) return null;

  const tiles = decodeTerrain(authored.teren, authored.sirina, authored.visina);
  if (!tiles) return null;

  const inside = (p: { x: number; y: number }): boolean =>
    p.x >= 0 && p.y >= 0 && p.x < authored.sirina && p.y < authored.visina;
  if (!inside(authored.dok)) return null;

  const spots = new Map<string, { x: number; y: number }>();
  for (const [nodeId, spot] of Object.entries(authored.mesta ?? {}))
    if (inside(spot)) spots.set(nodeId, { x: spot.x, y: spot.y });

  return {
    width: authored.sirina,
    height: authored.visina,
    tiles,
    dock: { x: authored.dok.x, y: authored.dok.y },
    spots,
    characters: (authored.likovi ?? []).filter(inside).map((person) => ({
      id: person.id,
      islandId: island.id,
      art: person.lik,
      x: person.x,
      y: person.y,
      facing: person.gleda ?? 'dole',
      name: person.ime ?? null,
      lines: person.poruke,
    })),
    props: (authored.ukrasi ?? []).filter(inside).map((prop) => ({
      kind: prop.vrsta,
      x: prop.x,
      y: prop.y,
    })),
  };
}

/** Flatten the authored content into exactly what one path's player sees. */
export function resolveContent(raw: RawContent, path: string): ResolvedContent {
  const allNodeIds = new Set<string>();
  for (const island of raw.ostrva) for (const node of island.cvorovi) allNodeIds.add(node.id);

  const islands: ResolvedIsland[] = raw.ostrva
    .filter((island) => island.putanje.includes(path))
    .sort((a, b) => a.redosled - b.redosled)
    .map((island, index) => {
      const nodes: ResolvedNode[] = island.cvorovi
        .filter((node) => !node.putanje || node.putanje.includes(path))
        .map((node) => {
          const lessonUrls = buildLessonUrls(node, path, raw.lessonUrlTemplate);
          const submission = node.predaja ?? 'nista';
          return {
            id: node.id,
            islandId: island.id,
            kind: node.tip,
            title: perPath<string>(node.naslov, path, node.id),
            estMin: perPath<number>(node.estMin, path, 0),
            submission,
            requires: node.zahteva ?? [],
            universal: node.univerzalno === true,
            preCompleted: node.unaprijedZavrsen === true,
            task: node.zadatak ? perPath<string>(node.zadatak, path, '') || null : null,
            note: node.napomena ?? null,
            skoolUrl: lessonUrls[0] ?? null,
            lessonUrls,
            optional: node.tip === 'opciono',
            posts: submission !== 'nista',
            needsRevision: submission === 'link+popravka',
            after: node.posle ?? null,
          };
        });

      return {
        id: island.id,
        name: island.naziv,
        order: island.redosled,
        day: island.dan ?? 0,
        worksRequired: island.potrebnoRadova ?? 0,
        souvenir: island.suvenir ?? null,
        goal: island.cilj ?? null,
        biome: island.biom ?? BIOMES[index % BIOMES.length]!,
        free: island.slobodno === true,
        map: island.mapa ?? { x: 0, y: 0 },
        world: resolveIslandWorld(island),
        nodes,
        layoutIds: island.cvorovi.map((node) => node.id),
      };
    })
    // An island whose nodes are all filtered out for this path does not exist for this path.
    .filter((island) => island.nodes.length > 0);

  return {
    version: raw.verzija ?? 1,
    communityUrl: raw.zajednicaUrl,
    paths: Object.entries(raw.putanje).map(([id, p]) => ({
      id,
      name: p.naziv,
      description: p.opis,
      active: p.aktivna !== false,
      note: p.napomena,
    })),
    islands,
    allNodeIds,
  };
}

/* ------------------------------------------------------------------ validation */

export interface ContentProblem {
  level: 'error' | 'warning';
  where: string;
  message: string;
  /** Stable id for the checks a UI needs to react to. Never shown; the message is what people read. */
  code?: string;
}

/**
 * Structural checks, one island at a time — a broken prerequisite here would strand a student
 * mid-island in production, which is the one failure mode with no recovery path.
 *
 * The messages are Serbian because they are read by the admin editing the world, not only by the
 * pre-commit script they were written for: the same list appears in ⚙ → Svet → Provera and in the
 * publish confirmation. Everything else in this codebase stays English; user-facing text does not.
 */
export function validateContent(raw: RawContent): ContentProblem[] {
  const problems: ContentProblem[] = [];
  const seenNodeIds = new Map<string, string>();
  const seenIslandIds = new Set<string>();
  const knownPaths = new Set(Object.keys(raw.putanje));

  const knownIslandIds = new Set(raw.ostrva.map((i) => i.id));

  for (const island of raw.ostrva) {
    if (seenIslandIds.has(island.id))
      problems.push({ level: 'error', where: island.id, message: 'dva ostrva imaju isti id' });
    seenIslandIds.add(island.id);

    /**
     * A free island is never crossed, so anything that pays out on crossing it is unreachable —
     * and a souvenir would still be handed over by `earnedSouvenirs`, which trusts a durable
     * `clearedIslands` row before it looks at the ratio.
     */
    if (island.slobodno) {
      if (island.suvenir)
        problems.push({
          level: 'error',
          where: island.id,
          message: 'slobodno ostrvo se nikad ne prelazi — suvenir se nikad ne bi dodelio',
        });
      // Without something to post, the lock card has nothing to offer and dead-ends again — which
      // is the whole reason this island exists.
      if (!island.cvorovi.some((n) => (n.predaja ?? 'nista') !== 'nista'))
        problems.push({
          level: 'error',
          where: island.id,
          message: 'slobodno ostrvo nema nijedan čvor koji se kači — nema šta da ponudi na bravi',
        });
    }

    for (const p of island.putanje)
      if (!knownPaths.has(p))
        problems.push({ level: 'error', where: island.id, message: `putanja "${p}" ne postoji` });

    const idsOnIsland = new Set(island.cvorovi.map((n) => n.id));

    for (const node of island.cvorovi) {
      const previous = seenNodeIds.get(node.id);
      if (previous)
        problems.push({
          level: 'error',
          where: node.id,
          message: `isti id već postoji na ostrvu "${previous}"`,
        });
      seenNodeIds.set(node.id, island.id);

      for (const requirement of node.zahteva ?? []) {
        if (!idsOnIsland.has(requirement))
          problems.push({
            level: 'error',
            where: node.id,
            message: `čeka "${requirement}", a taj čvor nije na istom ostrvu`,
          });
      }

      /**
       * „Jedan ključ" at node scale. `posle` and `zahteva` are two different reasons a node is not
       * available yet, and a node carrying both would be a node with two keys — the exact thing
       * GDD §2② forbids one level up.
       */
      if (node.posle) {
        if (!island.slobodno)
          problems.push({
            level: 'error',
            where: node.id,
            message: '„posle" radi samo na slobodnom ostrvu — na lancu redosled drži „zahteva"',
          });
        if ((node.zahteva ?? []).length > 0)
          problems.push({
            level: 'error',
            where: node.id,
            message: 'čvor ima i „posle" i „zahteva" — to su dva ključa na istom čvoru, ostavi jedan',
          });
        if (!knownIslandIds.has(node.posle))
          problems.push({
            level: 'error',
            where: node.id,
            // Silent otherwise: the node would simply never be offered to anybody, ever.
            message: `„posle" pokazuje na ostrvo "${node.posle}", a takvo ostrvo ne postoji`,
          });
      }

      if (node.tip === 'opciono' && (node.predaja ?? 'nista') !== 'nista')
        problems.push({
          level: 'warning',
          where: node.id,
          message: 'opcioni čvor traži predaju — nikad nije obavezan, pa se taj rad ne vidi nigde',
        });

      const estimates = typeof node.estMin === 'object' ? Object.values(node.estMin) : [node.estMin];
      if (estimates.some((m) => typeof m === 'number' && m === 0) && node.tip !== 'riznica')
        problems.push({
          level: 'warning',
          where: node.id,
          message: 'trajanje je 0 — filter „koliko vremena imaš“ ne može da ga postavi',
        });
    }

    // Prerequisite cycles inside an island would make nodes permanently unreachable.
    const cycle = findCycle(island.cvorovi);
    if (cycle)
      problems.push({
        level: 'error',
        where: island.id,
        // Named, not just detected: „cycle detected" is unactionable, the chain is a fix.
        message: `čvorovi čekaju jedan drugog u krug: ${cycle.join(' → ')}`,
      });
  }

  // Every path must have a reachable first island, otherwise onboarding dead-ends.
  for (const pathId of knownPaths) {
    if (raw.putanje[pathId]?.aktivna === false) continue;
    const has = raw.ostrva.some((i) => i.putanje.includes(pathId));
    if (!has)
      problems.push({
        level: 'error',
        where: pathId,
        message: 'putanja je aktivna, a nema ni jedno ostrvo',
      });
  }

  return problems;
}

function findCycle(nodes: RawNode[]): string[] | null {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const state = new Map<string, 0 | 1 | 2>();
  const stack: string[] = [];

  const visit = (id: string): string[] | null => {
    const current = state.get(id) ?? 0;
    if (current === 1) return [...stack.slice(stack.indexOf(id)), id];
    if (current === 2) return null;
    state.set(id, 1);
    stack.push(id);
    for (const next of byId.get(id)?.zahteva ?? []) {
      if (!byId.has(next)) continue;
      const found = visit(next);
      if (found) return found;
    }
    stack.pop();
    state.set(id, 2);
    return null;
  };

  for (const node of nodes) {
    const found = visit(node.id);
    if (found) return found;
  }
  return null;
}
